import { useState, useCallback } from 'react';
import { message } from 'antd';
import { m209Api, FM220, FM221, M209CreateRequest } from '../../../services/m209Api';
import { Mode } from './useM209Mode';

export function useM209Record() {
  const [record, setRecord] = useState<FM220 | null>(null);
  const [formData, setFormData] = useState<Partial<FM220>>({});
  const [details, setDetails] = useState<Partial<FM221>[]>([]);
  const [loading, setLoading] = useState(false);

  /** Load a single record by ID */
  const loadRecord = useCallback(async (id: string) => {
    setLoading(true);
    try {
      const data = await m209Api.get(id);
      setRecord(data);
      setFormData({ ...data });
      setDetails(data.details || []);
    } catch {
      message.error('載入失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  /** Set a single master field */
  const setField = useCallback((key: keyof FM220, value: FM220[keyof FM220]) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  /** Get a single master field value */
  const getField = useCallback((key: keyof FM220): FM220[keyof FM220] => {
    return (formData as Record<string, FM220[keyof FM220]>)[key] ?? '';
  }, [formData]);

  /** Build API request from form state */
  const buildRequest = useCallback((): M209CreateRequest => {
    return {
      fm220_11: (formData.fm220_11 as string) || null,
      fm220_12: (formData.fm220_12 as string) || '',
      fm220_13: (formData.fm220_13 as string) || '',
      fm220_14: (formData.fm220_14 as string) || '',
      details: details as FM221[],
    };
  }, [formData, details]);

  /** Save (create or update) */
  const handleSave = useCallback(async (
    mode: Mode,
    onSuccess: (result: FM220) => void,
  ) => {
    setLoading(true);
    try {
      const req = buildRequest();
      if (mode === 'add') {
        const result = await m209Api.create(req);
        message.success(`新增成功: ${result.fm220_01}`);
        onSuccess(result);
      } else {
        const result = await m209Api.update(formData.fm220_01!, req);
        message.success('修改成功');
        onSuccess(result);
      }
    } catch (err: unknown) {
      const errMsg = err instanceof Error ? err.message : '儲存失敗';
      message.error(errMsg);
    } finally {
      setLoading(false);
    }
  }, [formData, buildRequest]);

  /** Delete current record */
  const handleDelete = useCallback(async (onSuccess: () => void) => {
    if (!record?.fm220_01) return;
    setLoading(true);
    try {
      await m209Api.delete(record.fm220_01);
      message.success('刪除成功');
      onSuccess();
    } catch {
      message.error('刪除失敗');
    } finally {
      setLoading(false);
    }
  }, [record]);

  /** Revert form data to last loaded record */
  const revertToRecord = useCallback(() => {
    if (record) {
      setFormData({ ...record });
      setDetails(record.details || []);
    }
  }, [record]);

  /** Reset form for add mode */
  const resetForAdd = useCallback(() => {
    setRecord(null);
    setFormData({ fm220_80: '' });
    setDetails([]);
  }, []);

  /** Apply result after save */
  const applyResult = useCallback((result: FM220) => {
    setRecord(result);
    setFormData({ ...result });
    setDetails(result.details || []);
  }, []);

  /** Add a detail row */
  const addDetailRow = useCallback(() => {
    setDetails(prev => [
      ...prev,
      {
        fm221_02: prev.length + 1,
        fm221_11: '',
        fm221_12: '',
        fm221_14: '',
        fm221_21: '',
        fm221_22: '',
      },
    ]);
  }, []);

  /** Delete a detail row by index */
  const deleteDetailRow = useCallback((idx: number) => {
    setDetails(prev => prev.filter((_, i) => i !== idx));
  }, []);

  return {
    record, formData, details, loading,
    setDetails,
    loadRecord, setField, getField,
    handleSave, handleDelete,
    revertToRecord, resetForAdd, applyResult,
    addDetailRow, deleteDetailRow,
  };
}
